import React from 'react'
import { Action, any, async, AsyncState, IOWidget, mkWidget, mk_widget } from 'widgets-for-react'
import { BlockData, CMSState } from '../../../../cms/blocks/types.gen'
import { PublicSite_CustomPageData } from '../../../application.mod'
import { PublicSiteState } from '../../../../public_site.main.mod'
import { WordPress } from '../../../../wordpress/wordpress_api'
import { StyleguideImgPlaceholder } from '../../../../../styleguide/components/helpers'
import { ExtraImages, getImageSize } from '../../../utils/extra_images.mod'
import { videoLibraryBlockData } from '../types.mod'
import VideoLightbox, { VideoProvider } from '../../../templates/videoLightbox'

export type VideoLibraryBlockProps = {
  block_index: number,
  video_id: string,
  title: string,
  intro: string,
  video: AsyncState<string[]>,
  image: WordPress.Image,
  show_highlight: boolean,
  selected_video_type: VideoProvider,
  selected_video_url: string
  isOpen: 'open' | 'close'

  selectedTag: string
  video_filters: AsyncState<string[]>
  items: AsyncState<VideoLibraryItemProps[]>

  show_in_page_navigation: boolean
  navigation_title: string
}

export const AllVideosText: string = "Alle video's"

export type VideoLibraryItemProps = {
  video_id: string,
  link_text: string,
  link_url: string,
  tag: string,
  video_type: string,
}

export type VideoLibraryBlockStyle = Partial<{
  block_classname: string
  inner_classname: string
  header_classname: string
  title_classname: string
  content_classname: string
}>

export function VideoLibraryBlock<customBlockData, extra_images>(
  props: VideoLibraryBlockProps,
  options?: { style: VideoLibraryBlockStyle; key?: string }
): (s: CMSState<customBlockData, extra_images, PublicSite_CustomPageData>) => IOWidget<PublicSiteState, Action<CMSState<customBlockData, extra_images, PublicSite_CustomPageData>>> {
  return (s: CMSState<customBlockData, extra_images, PublicSite_CustomPageData>) => (public_state: PublicSiteState) => {

    return any<Action<VideoLibraryBlockProps>>()([

      async<VideoLibraryItemProps[]>()(props.items).map<Action<VideoLibraryBlockProps>>(u =>
        props =>
          ({ ...props, items: u(props.items) })
      ),

      async<string[]>()(props.video_filters).map<Action<VideoLibraryBlockProps>>(u =>
        props =>
          ({ ...props, video_filters: u(props.video_filters) })
      ),

      async<string[]>()(props.video).map(u =>
        props =>
          ({ ...props, video: u(props.video) })
      ),

      mk_widget({
        run: setState => (
          <section className="block block--library">
            <div className="block__inner container container--l grid--block">

              {props.show_highlight &&
                props.video.kind == "loaded" ?
                videoHighlight(props.title, props.intro, props.image, props.video.value, setState)
                : <></>
              }

              <div className="filters">
                <span className="sr-text">Tags van videos</span>
                <ul className="filters__list">
                  {
                    props.video_filters.kind == "loaded" ?
                      props.video_filters.value.map((filter) => {
                        return videoFilter(filter, props.selectedTag, props.selectedTag == filter, setState)
                      })
                      : <></>
                  }
                </ul>
              </div>

              <ul className="block__grid grid c--columns">
                {
                  props.items.kind == "loaded" ?
                    videoLibrary(props.items.value, props.selectedTag, setState)
                    : <></>
                }
              </ul>
            </div>
          </section>
        )
      }),

      mk_widget({
        run: setState => (
          <>
            {(props.selected_video_type && props.selected_video_url) &&
              props.selected_video_type != "" &&
              props.selected_video_url != "" && (
                <VideoLightbox
                  provider={props.selected_video_type}
                  videoURL={props.selected_video_url}
                  closeText="Close lightbox"
                  isOpen={props.isOpen}
                  setState={setState}
                />
              )}
          </>
        ),
      })

    ]).map(u => os => {
      let current = os.blocks.get(props.block_index) as videoLibraryBlockData
      let newBlock: videoLibraryBlockData = { ...current, value: { ...current.value, fst: u(current.value.fst) } }
      let newState: typeof os = { ...os, blocks: os.blocks.set(props.block_index, newBlock as videoLibraryBlockData) }
      return newState
    })
  }
}

export const videoHighlight = (title: string,
  intro: string,
  image: WordPress.Image,
  video: string[],
  setState: (output: Action<VideoLibraryBlockProps>) => void
) => {
  return (
    <div className="block__highlight highlight">
      <div className="highlight__inner">
        <header className="highlight__header item--2">
          <h2 className="block__title highlight__title typo--l" dangerouslySetInnerHTML={{ __html: title }} />
          <p className="highlight__intro text--styled" dangerouslySetInnerHTML={{ __html: intro }} />
        </header>
        <div className="highlight__visual item--1">

          <a onClick={a =>
            setState(s1 => ({
              ...s1, isOpen: "open", selected_video_url: video[0], selected_video_type: video[1]
            }))}>
            {image &&
              <img
                src={getImageSize<ExtraImages>(image, '1400x510')}
                data-src={getImageSize<ExtraImages>(image, '580x440')}
                data-srcset={[
                  [getImageSize<ExtraImages>(image, '580x440') + ' 5800w'],
                  [getImageSize<ExtraImages>(image, '880x510') + ' 880w'],
                  [getImageSize<ExtraImages>(image, '1400x510') + ' 1400w'],
                ]}
                data-sizes="auto"
                width="580"
                height="440"
                className="highlight__image"
              />
            }
          </a>
        </div>
      </div>
    </div>
  )
}

export const videoFilter = (
  tag: string,
  selectedTag: string,
  active: boolean,
  setState: (output: Action<VideoLibraryBlockProps>) => void
) => {
  return (
    <li id={`list-item-${tag}`} className="filter">
      <label id={`label-${tag}`} className={`filter__tag btn btn--tertiary${active === true ? " btn--active" : ""}`} htmlFor={tag}>
        <input
          type="checkbox"
          checked={selectedTag == tag}
          className="filter__checkbox"
          id={tag}
          onChange={(e) => {
            if ((e.target as HTMLInputElement).checked) {
              setState(s1 => ({ ...s1, selectedTag: tag }))
            }
          }}
        />
        {tag}
      </label>
    </li>
  )
}

export function videoLibrary(
  videoLibrary: VideoLibraryItemProps[],
  selectedTag: string,
  setState: (output: Action<VideoLibraryBlockProps>) => void) {
  return (
    <>
      {videoLibrary && (
        videoLibrary.filter(a => selectedTag == AllVideosText ? a.tag != selectedTag : a.tag == selectedTag).map(item => {
          return <li className="grid__item item">
            <article className="grid__article">
              {VideoLibraryItem(item, selectedTag == item.tag ? true : false, setState)}
            </article>
          </li >
        })
      )}
    </>
  )
}

export function VideoLibraryItem(itemProps: VideoLibraryItemProps,
  filterActive: boolean,
  setState: (output: Action<VideoLibraryBlockProps>) => void) {
  return (
    <>
      <a className={`grid__inner grid__link grid__link--image`}
        onClick={a => {
          setState(s1 => ({ ...s1, selected_video_url: itemProps.link_url, selected_video_type: itemProps.video_type, isOpen: "open" }))
          document.documentElement.classList.add('lightbox--open')
        }

        }>
        <div className={`grid__content grid__content--image ${filterActive ? 'active' : ''}`}>
          <header className="grid__header">
            <h3 className="grid__title typo--m" dangerouslySetInnerHTML={{ __html: itemProps.link_text }} />
          </header>

          <svg
            className={`grid__icon`}
            width="25px"
            height="14px"
            aria-hidden="true"
          >
            <use xlinkHref="#icon--arrow" />
          </svg>
        </div>
        <div className="grid__visual">

          {
            itemProps.video_type == "youtube" ?
              <img className="grid__image"
                src={`https://img.youtube.com/vi/${itemProps.link_url.split("/").pop().replace("watch?v=", "").split("&")[0]}/hqdefault.jpg`} width="440" height="255" alt="" />
              : <img className="grid__image" src={StyleguideImgPlaceholder({ shape: { width: 440, height: 255, radius: '0', bgColor: '#9CD1C2' }, text: { color: '#1e1e1e', font: 'Open Sans, sans-serif' } })} width="440" height="255" alt="" />
          }
        </div>
      </a>
    </>
  )
}



// WEBPACK FOOTER //
// ./Client/components/public_site/blocks/custom_blocks/widgets/videoLibraryBlock.tsx